import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from 'recharts';
import { reports, orders as ordersApi } from '../api/endpoints';
import { useOrderEvents } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { StatCard, Loading, StatusBadge, money, dateTime, Empty } from '../components/Common';

export default function Dashboard() {
  const { admin, storeName } = useAuth();
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState([]);
  const [topServices, setTopServices] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [dash, salesRes, servicesRes, recentRes] = await Promise.all([
        reports.dashboard(),
        reports.sales({ groupBy: 'day' }),
        reports.services(),
        ordersApi.list({ limit: 8 }),
      ]);
      setStats(dash.data);
      setSales(salesRes.data.rows.map((r) => ({ date: r._id.slice(5), revenue: r.revenue, orders: r.orders })));
      setTopServices(servicesRes.data.rows.slice(0, 6));
      setRecent(recentRes.data.orders);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // A new order should move these numbers without anyone pressing refresh.
  useOrderEvents(() => load());

  useEffect(() => { document.title = 'Dashboard · UrbanClean'; }, []);

  if (loading) return <Loading label="Loading dashboard" />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Welcome back, {admin?.name?.split(' ')[0]}</h1>
          <div className="sub">
            {storeName ? `${storeName} · live overview` : 'Platform-wide live overview'}
          </div>
        </div>
        <Link className="btn btn-blue" to="/orders">View all orders</Link>
      </div>

      <div className="grid grid-4 mb">
        <StatCard tone="navy" label="Today's orders" value={stats.today.orders} sub={`${money(stats.today.revenue)} collected`} />
        <StatCard tone="blue" label="This month" value={stats.month.orders} sub={money(stats.month.revenue)} />
        <StatCard tone="amber" label="Active orders" value={stats.activeOrders} sub={`${stats.pendingPickups} awaiting pickup`} />
        <StatCard tone="green" label="Partners online" value={stats.partnersOnline} sub={`${stats.customers} customers`} />
      </div>

      <div className="grid grid-2 mb">
        <div className="card">
          <div className="card-head"><h3>Revenue — last 30 days</h3></div>
          <div className="card-body" style={{ height: 268 }}>
            {sales.length === 0 ? <Empty title="No sales in this period" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sales} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3182c8" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#3182c8" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f2" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7a95' }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7a95' }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v, n) => (n === 'revenue' ? money(v) : v)} />
                  <Area type="monotone" dataKey="revenue" stroke="#3182c8" strokeWidth={2.5} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h3>Top services by revenue</h3></div>
          <div className="card-body" style={{ height: 268 }}>
            {topServices.length === 0 ? <Empty title="No service data yet" /> : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topServices} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f2" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6b7a95' }} tickLine={false} axisLine={false} interval={0} angle={-16} textAnchor="end" height={54} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7a95' }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Bar dataKey="revenue" fill="#1f3468" radius={[5, 5, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>Latest orders</h3>
          <Link className="btn btn-sm btn-ghost" to="/orders">See all</Link>
        </div>
        {recent.length === 0 ? <Empty title="No orders yet" body="New bookings will appear here the moment they land." /> : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Order</th><th>Customer</th><th>Type</th>
                  <th>Status</th><th className="right">Total</th><th>Placed</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((o) => (
                  <tr key={o._id}>
                    <td><Link to={`/orders/${o._id}`} className="mono strong">{o.orderNumber}</Link></td>
                    <td>{o.customerName || '—'}<div className="muted" style={{ fontSize: 12 }}>{o.customerMobile}</div></td>
                    <td><span className="badge badge-gray">{o.type === 'walk-in' ? 'Walk-in' : 'App'}</span></td>
                    <td><StatusBadge status={o.status} /></td>
                    <td className="right strong">{money(o.total)}</td>
                    <td className="muted nowrap">{dateTime(o.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
